import { commonDownloadValueFormatter, getPathFromDashboard } from "@/pages/dashboard/utils/utils";
import { AXISLABEL_FORMATTER_MAP, LOG_TYPE_MAP, RANGE_FILTER_ARR, RANGE_FILTER_PARAMS_MAP } from "@/pages/benchmark/_config/constant";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { isNil } from "lodash-es";
import moment from "moment";
import { PAGE_TYPE_AUTH_PATH } from '@/pages/dashboard/_config/constant';
/**
 * benchmark路径解析
 * @param str 路径
 * @returns 以/分割路径解析字段
 */
export function parseBenchmarkUrlInfo(str = '') {
  const [_null, system, urlType, ...arr] = str.trim().split('/');
  const realPath = arr.length > 0 ? arr.join('/') : '';
  const currentPath = arr.length > 0 ? `/${arr.join('/')}` : '';
  return {
    system,
    urlType,
    realPath,
    currentPath
  };
}

/**
 * 获取菜单中所有报表
 * @param menuList 菜单
 * @returns 报表数组
 */
export function flattenMenu(menuList: any[]) {
  const result: any[] = [];

  function traverse(menu: { subMenus: any[]; }) {
    !menu?.subMenus?.length && result?.push(menu);
    if (menu?.subMenus && menu?.subMenus?.length > 0) {
      menu?.subMenus?.forEach((subMenu: any) => traverse(subMenu));
    }
  }

  menuList?.forEach((menu: any) => traverse(menu));
  return result;
}

/**
 * 菜单数据处理
 * @param list 菜单
 * @returns 格式化后菜单
 */
export const handleMenuList = (list = []) => {
  const menuList: any[] = list?.map((item: any) => {
    const { u_id: id, name_en: title, name_cn: titleCN, dashoboard_list: subMenus, menu_type: menuType, page_type: pageType, path, new_flag: newFlag } = item;
    const routePath = getPathFromDashboard(item);
    const authPath = pageType === PAGE_TYPE_AUTH_PATH ? (path || '') : '';
    const processedSubMenus = handleMenuList(subMenus);
    // 子目录冒泡：如果子菜单中有 showNew，父级也标记 showNew
    const hasNewChild = processedSubMenus?.some((sub: any) => sub?.showNew);
    return {
      ...item,
      frameType: menuType === "1" && pageType === "path" && path?.startsWith('/') ? path?.slice(1) : "",
      id,
      title,
      titleCN,
      // 特性报表也需要设置path，用于匹配选中菜单
      path: routePath,
      auth_path: authPath,
      // NEW 标签：后端 new_flag 为 true 或子菜单有 new 则显示
      showNew: !!newFlag || hasNewChild,
      // 预发布和正式需要受控展示
      env: ['pre', 'pro'],
      subMenus: processedSubMenus,
    };
  });
  return menuList || [];
};



// 范围输入筛选器的值的处理
const getRangeFilterValue = (min: string | null, max: string | null, radioSelectedValue: string): string[] => {
  const type = RANGE_FILTER_PARAMS_MAP?.[radioSelectedValue] || '';
  const raidoType = type ? [type] : [];
  if ((isNil(min) || min === '') && (isNil(max) || max === '')) {
    return [];
  }
  // 大于: gt  小于: lt 等于: eq 大于等于: ge 小于等于: le
  if (isNil(max) || max === '') {
    return [`ge ${min}`, ...raidoType];
  }
  if (isNil(min) || min === '') {
    return [`le ${max}`, ...raidoType];
  }
  if (max === min) {
    return [`eq ${min}`, ...raidoType];
  }
  return [`ge ${min} and le ${max}`, ...raidoType];

};

/**
 * 格式化游戏类型和子类型的值
 * @param data - 包含游戏类型和子类型的字符串数组，格式为 "flag:gameType"
 * @returns 返回一个字符串数组，其中每个元素是合并后的 "flag:gameType1|gameType2|..."
 * 
 * 该函数首先创建一个Map来存储flag和对应的游戏类型数组，
 * 然后遍历输入的数据数组，将每个元素按冒号分割成flag和gameType，
 * 并将gameType添加到对应flag的数组中。
 * 最后，遍历Map，将每个flag和对应的游戏类型数组合并成一个由'|'分隔的字符串，
 * 并将合并后的结果添加到新的数组中返回。
 */
const formatGenreSubgenreVal = (data: any[]) => {
  const flagMap = new Map(); // 创建一个Map来存储flag和对应的游戏类型数组
  // 遍历数据数组
  data.forEach(item => {
    // 使用冒号分割flag和游戏类型
    const [flag, gameType] = item.split(':');
    // 如果Map中没有这个flag，就添加一个新的键值对
    if (!flagMap.has(flag)) {
      flagMap.set(flag, []);
    }
    // 将游戏类型添加到对应flag的数组中
    flagMap.get(flag).push(gameType);
  });
  // 创建一个新的数组来存储合并后的结果
  const mergedData: string[] = [];
  // 遍历Map，将每个flag和对应的游戏类型数组合并成一个字符串
  flagMap.forEach((gameTypes, flag) => {
    // 使用join方法将游戏类型数组合并成一个由'|'分隔的字符串
    const mergedGameTypes = gameTypes.join('|');
    // 将合并后的结果添加到新的数组中
    mergedData.push(`${flag}:${mergedGameTypes}`);
  });
  return mergedData; // 返回合并后的结果数组
};

// 详情页筛选器公共formatter方法
/**
 * 格式化筛选值
 * @param filterValue - 任意类型的筛选值对象
 * @returns 格式化后的对象，其中键为原键加上 "_list" 后缀，值为对应的筛选值数组
 * 
 * 该函数用于将传入的筛选值对象进行格式化处理。
 * 对于单选下拉的情况，将值放入数组中。
 * 对于特定的 "genre_subgenre" 键，根据子类型的值进行特殊处理。
 * 如果键在预定义的范围筛选数组中，按照特定规则处理范围值。
 * 其他情况直接将值赋给对应的键。
 */
export const formatFilterValues = (filterValue: any): any => {
  const obj: { [key: string]: string[]; } = {};
  (Object.keys(filterValue || {})).forEach((key: string) => {
    // 单选下拉
    const keyList = `${key}_list`;
    if (typeof filterValue[key] === 'string') {
      obj[keyList] = [filterValue[key]];
    } else if (key === 'genre_subgenre') {
      const subgenreVal = filterValue[key]?.subgenre;
      // 后端反馈，全选时传空，在组件中设置默认值为[] 的时候级联筛选器选不中all，所以设置255后在这里转换处理
      obj.genre_list = (subgenreVal?.length === 1 && subgenreVal?.[0] === '255') ? [] : formatGenreSubgenreVal(filterValue.genre_subgenre.subgenre);
    } else if (key === 'game') {
      const gameValue = filterValue[key];
      // 后端反馈，全选时传空，在组件中设置默认值为[] 的时候级联筛选器选不中all，所以设置255后在这里转换处理
      obj[keyList] = (gameValue?.length === 1 && gameValue?.[0] === '255') ? [] : gameValue;
    } else if (RANGE_FILTER_ARR?.includes(key)) {
      const { inputValue, radioSelectedValue } = filterValue[key] || {};
      const { max, min } = inputValue;
      obj[keyList] = getRangeFilterValue(min, max, radioSelectedValue);
    } else {
      obj[keyList] = filterValue[key];
    };
  });
  return obj;
};

// object转换链接params
export const formatObjUrl = (obj: Record<string, any>): string => {
  if (Object.keys(obj).length === 0) {
    return ''; // Return an empty string for an empty object
  }
  const queryString = `${Object.entries(obj)
    .map(([key, value]) => {
      const formattedValue = Array.isArray(value)
        ? value.map(v => v).join(',')
        : value;
      return `${key}=${formattedValue}`;
    })
    .join('&')}&`;

  return queryString.endsWith('&') ? queryString.slice(0, -1) : queryString;
};

/**
 * 归类前端数值格式化
 * @param formatRule 格式化规则
 * @param scalarType 指标数据类型
 * @returns 
 */
export const getDownValueType = (formatRule: string, scalarType: string) => {
  // 配置规则情况下
  if (formatRule) {
    if (["thousandsInit", "int", "bmk", "bm"]?.includes(formatRule)) {
      return 'int';
    }
    if (formatRule?.toLowerCase()?.includes("percent")) {
      return 'percent';
    }
    return "decimals";
  }
  if (scalarType === 'percentage') {
    return 'percent';
  }
  return "decimals";
};

/**
 * 表格下载数值格式化（根据配置格式化）
 * @param value 数值
 * @param metricConfig  指标配置
 * @returns 根据配置格式化后excel数值
 */
export const downValueFormatter = (value: number | string, metricConfig: { formatter: string; scalar_type: string; }) => {
  const { formatter, scalar_type: scalarType } = metricConfig || {};
  const valueType = getDownValueType(formatter, scalarType);
  const downloadValue = commonDownloadValueFormatter(valueType, value) || value;
  return downloadValue;
};

/**
 * 获取打点typeId
 * @param frameType 框架类型
 * @returns typeId
 */
export const getLogTypeId = (frameType: string | null) => `benchmark_${LOG_TYPE_MAP?.[frameType || "pcconsole"]}`;

/**
 * 获取指标格式化规则
 * @param metricConfig 指标配置
 * @returns 指标格式化规则
 */
export const getMetricFormatRule = (metricConfig: { formatter: string; scalar_type: string; }) => {
  const { formatter, scalar_type: scalarType } = metricConfig || {};
  // 格式化规则
  const metricFormatRule = scalarType === 'percentage' ? 'percent2Decimal' : 'thousandsInit';
  const formatRule = formatter || metricFormatRule;
  return formatRule;
};

/**
 * 指标数值格式化（根据配置格式化）
 * @param value 数值
 * @param metricConfig  指标配置
 * @returns 根据配置格式化后数值
 */
export const metricValueFormatter = (value: number | string, metricConfig: { formatter: string; scalar_type: string; }) => {
  // 格式化规则
  const formatRule = getMetricFormatRule(metricConfig);
  return dataFormat({ value, formatRule });
};

/**
 * 数值坐标轴格式化
 * @param value 数值
 * @param metricConfig 指标配置
 * @returns 格式化后数值
 */
export const axisLabelFormatter = (value: number | string, metricConfig: { formatter: string; scalar_type: string; }) => {
  const { formatter, scalar_type: scalarType } = metricConfig || {};
  const metricFormatRule = scalarType === 'percentage' ? 'percent2Decimal' : 'bmk0';
  // 映射坐标轴配置（原因：传入fillZero: false保留位数不填充0）
  const formatRule = AXISLABEL_FORMATTER_MAP?.[formatter || metricFormatRule] || formatter || metricFormatRule;
  return dataFormat({ value, formatRule, fillZero: false });
};

/**
 * 逗号字符串转换成数组
 * @param value 逗号字符串
 * @returns 格式化后数组
 */
export const valueTypeArray = (value: string) => value.trim().split(',').map((item: string) => item.trim()) ?? [];

/**
 * 检查指标是否正确用于重定向benchmark首页
 * @param metricKey 指标key
 * @param metric 指标url
 * @param metricConfig 指标配置
 * @param validTypes 正确指标
 * @returns 格式化后数组
 */
export const isValidMetric = (metricKey: string, metric: string, metricConfig: any, validTypes: string[]): boolean => {
  const valueTypeArray = (metricConfig?.[metric]?.value_type || '').split(',').map((item: string) => item.trim());
  const valueTypesSet = new Set(valueTypeArray);
  const hasOnlyValidTypes = validTypes.every(type => valueTypesSet.has(type)) && valueTypesSet.size === validTypes.length;
  return (metricKey !== metric) || !metricConfig?.[metric] || hasOnlyValidTypes;
};

// 获取指标名称宽度
export const nameWidth = (name = '', isEn = true) => {
  if (isEn) {
    if (name?.length <= 20) {
      return 200;
    }
    if (name?.length <= 35) {
      return 340;
    }
    return name?.length * 10;
  }
  if (name?.length <= 10) {
    return 200;
  }
  if (name?.length <= 20) {
    return 340;
  }
  return name?.length * 15;
};

// 获取发布日期筛选器值
export const getReleaseTimeFilterValue = ({
  startDate,
  endDate,
  dateType,
}: {
  startDate: string;
  endDate: string;
  dateType: string;
}) => {
  // 如果是 month 类型，把 endDate 改成当月最后一天
  const adjustedEndDate =
    dateType === 'month' && endDate
      ? moment(endDate).endOf('month').format('YYYY-MM-DD')
      : endDate;

  const format = (date: string | null) =>
    date ? moment(date).format('YYYY-MM-DD') : '';

  const formattedMin = format(startDate);
  const formattedMax = format(adjustedEndDate);

  if (!formattedMin && !formattedMax) {
    return [];
  }

  if (!formattedMax) {
    return [`ge ${formattedMin}`];
  }

  if (!formattedMin) {
    return [`le ${formattedMax}`];
  }

  if (formattedMin === formattedMax) {
    return [`eq ${formattedMin}`];
  }

  return [`ge ${formattedMin} and le ${formattedMax}`];
};

/**
 * 解析值，将百分比字符串转换为数值，保留原始格式信息
 * @param value 原始值
 * @returns { numericValue: 数值, isPercent: 是否百分比, originalValue: 原始值 }
 */
export const parseChartValue = (value: any) => {
  if (value === undefined || value === null || value === '') {
    return { numericValue: undefined, isPercent: false, originalValue: value };
  }
  
  // 如果已经是数字，直接返回
  if (typeof value === 'number') {
    return { numericValue: value, isPercent: false, originalValue: value };
  }
  
  // 字符串处理
  if (typeof value === 'string') {
    const trimmed = value.trim();
    
    // 检查是否是百分比格式 (如 "3.5%", "10%")
    if (trimmed.endsWith('%')) {
      const numStr = trimmed.slice(0, -1);
      const num = parseFloat(numStr);
      if (!isNaN(num)) {
        return { numericValue: num, isPercent: true, originalValue: value };
      }
    }
    
    // 尝试解析为普通数字
    const num = parseFloat(trimmed);
    if (!isNaN(num)) {
      return { numericValue: num, isPercent: false, originalValue: value };
    }
  }
  
  // 无法解析的值
  return { numericValue: undefined, isPercent: false, originalValue: value };
};

/**
 * 从数据对象中获取值，支持从 text_value_json 中取值
 * @param dataItem 数据项对象
 * @param dataKey 数据 key
 * @returns 对应的值
 */
export const getValueFromData = (dataItem: any, dataKey: string) => {
  if (!dataItem) return undefined;
  
  // 优先从 text_value_json 中取值
  if (dataItem.text_value_json?.[dataKey] !== undefined) {
    return dataItem.text_value_json[dataKey];
  }
  
  // 否则从顶层取值
  return dataItem[dataKey];
};